/**
 * Formatting for the values the API speaks in.
 *
 * Money crosses the wire as integer USD cents and times as minutes after
 * midnight, so every screen needs the same two conversions. Kept here rather
 * than per-app, because a rounding or rounding-direction difference between
 * the public site and the admin would show up as two prices for one booking.
 */

/**
 * 1999 → "$19.99", 4631100 → "$46,311.00". Whole amounts keep the decimals, so
 * columns line up; thousands are grouped, as the exports' `$#,##0.00` does.
 */
export const formatCents = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * 600 → "10:00 AM". Minutes after midnight, in the business timezone already.
 * Wraps at a day, so a 1440 closing time reads "12:00 AM" rather than "12:00 PM".
 */
export function clockLabel(minutes: number): string {
  const total = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const hour = Math.floor(total / 60);
  const minute = total % 60;
  const suffix = hour < 12 ? 'AM' : 'PM';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${String(minute).padStart(2, '0')} ${suffix}`;
}

/** 600, 720 → "10:00 AM – 12:00 PM". An en dash, because it is a range. */
export const clockRange = (fromMinutes: number, toMinutes: number) =>
  `${clockLabel(fromMinutes)} – ${clockLabel(toMinutes)}`;

/**
 * A `yyyy-mm-dd` key as a date, read as a plain calendar date.
 *
 * Parsed field by field rather than through `new Date(key)`, which reads the
 * string as UTC midnight and then renders it in the viewer's timezone — which
 * puts anyone west of Greenwich on the previous day.
 */
export function parseDateKey(key: string): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * A Date as "15/06/26" — dd/mm/yy, the one way both apps write a date.
 * Built by hand rather than through `toLocaleDateString`, which follows the
 * viewer's locale and would hand an American browser mm/dd.
 */
export const ddmmyy = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear() % 100).padStart(2, '0')}`;

/** "2026-06-15" → "Mon 15/06/26". */
export const dateLabel = (key: string) => {
  const d = parseDateKey(key);
  return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${ddmmyy(d)}`;
};

/** "2026-06-15" → "15/06/26". */
export const longDateLabel = (key: string) => ddmmyy(parseDateKey(key));

/** "2026-06" → "June 2026". */
export const monthLabel = (monthKey: string) =>
  parseDateKey(`${monthKey}-01`).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

/** A `yyyy-mm-dd` key for a Date, in its own timezone rather than UTC. */
export const dateKeyOf = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** How long a booking runs, said the way a person would: "1 hour 30 minutes". */
export function durationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const parts = [];
  if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (rest) parts.push(`${rest} minutes`);
  return parts.join(' ') || '0 minutes';
}
